import type { Theme } from '@mui/material';

import type {
    DesignSystemCardSize,
    DesignSystemEmptyStateSize,
    DesignSystemEmptyStateType,
} from '../../theme/design-system/types';
import { spacingValues } from '../../theme/design-system/tokens';
import type { DesignSystemLeadItemSize } from '../LeadItem/LeadItem.styleUtils';
import type { TypographyProps } from '../Typography/Typography';
import type { FeatureIconStyle } from '../LeadItem/LeadItem';

export const sizeToCardSize: Record<
    DesignSystemEmptyStateSize,
    DesignSystemCardSize
> = {
    xl: 'md',
    md: 'sm',
    sm: 'xs',
};

export const sizeToLeadItemSize: Record<
    DesignSystemEmptyStateSize,
    DesignSystemLeadItemSize
> = {
    xl: 'xl',
    md: 'lg',
    sm: 'md',
};

export const titleVariantMap: Record<
    DesignSystemEmptyStateSize,
    TypographyProps['variant']
> = {
    xl: 'h5',
    md: 'md',
    sm: 'sm',
};

export const descriptionVariantMap: Record<
    DesignSystemEmptyStateSize,
    TypographyProps['variant']
> = {
    xl: 'md',
    md: 'sm',
    sm: 'xs',
};

export const contentGapMap: Record<DesignSystemEmptyStateSize, string> = {
    xl: spacingValues['3xl'],
    md: spacingValues['2xl'],
    sm: spacingValues.xl,
};

type RingConfig = {
    sizes: number[];
    opacities: number[];
};

export const ringConfigMap: Record<
    DesignSystemEmptyStateSize,
    RingConfig | null
> = {
    xl: { sizes: [128, 108, 86], opacities: [0.3, 0.5, 0.8] },
    md: { sizes: [96, 80, 64], opacities: [0.3, 0.5, 0.8] },
    sm: null,
};

const RING_COLOR_ACCENT = '#D6D0F3';
const RING_COLOR_NEUTRAL = '#D4D4D8';

export function getRingColor(type: DesignSystemEmptyStateType) {
    return type === 'error' ? RING_COLOR_NEUTRAL : RING_COLOR_ACCENT;
}

type TypeStyleConfig = {
    featureIconStyle: FeatureIconStyle;
    colorScheme: 'accent1' | 'critical';
};

const typeToFeatureIcon: Record<DesignSystemEmptyStateType, TypeStyleConfig> = {
    default: { featureIconStyle: 'stroke', colorScheme: 'accent1' },
    highlighted: { featureIconStyle: 'stroke', colorScheme: 'accent1' },
    error: { featureIconStyle: 'fill-light', colorScheme: 'critical' },
};

export function getFeatureIconConfig(type: DesignSystemEmptyStateType) {
    return typeToFeatureIcon[type];
}

export function getTypeStyles(type: DesignSystemEmptyStateType, theme: Theme) {
    switch (type) {
        case 'highlighted':
            return {
                border: `1px solid ${theme.palette.ds.border.accent1.tertiary}`,
                background: `linear-gradient(90deg, white 0%, ${theme.palette.ds.bg.accent1.tertiary} 100%)`,
            };

        case 'error':
            return {
                border: `1px solid ${theme.palette.ds.border.neutral.primary}`,
                backgroundColor: theme.palette.ds.bg.neutral.primary,
            };

        case 'default':
        default:
            return {
                border: `1px solid ${theme.palette.ds.border.neutral.primary}`,
                backgroundColor: theme.palette.ds.bg.neutral.primary,
            };
    }
}
